import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  hospitalAdminApi,
  type MetaEventLogRow,
  type MetaEventQueueStatus,
  type MetaThmsAction,
} from "@/api/hospitalAdmin";
import { toast } from "sonner";

const ACTION_LABELS: Record<MetaThmsAction, string> = {
  CRM_LEAD_CREATED: "Qualified lead (Interested / Follow Up)",
  PATIENT_SELF_REGISTRATION: "Self registration",
  CALLBACK_REQUEST: "Contacted",
  APPOINTMENT_BOOKED: "Appointment booked",
  OPD_CONSULTATION_COMPLETED: "OPD consultation",
  DAY_CARE_REGISTRATION_COMPLETED: "Day care completed",
  IPD_ADMISSION_CREATED: "IPD admission",
  SURGERY_SCHEDULED: "Surgery scheduled",
  SURGERY_COMPLETED: "Surgery completed",
  IPD_FINAL_BILL_FROZEN: "Bill frozen",
  IPD_PAYMENT_RECEIVED: "IPD payment",
  LOYALTY_APP_REGISTRATION: "App registration",
};

function statusClass(status: MetaEventQueueStatus) {
  switch (status) {
    case "SENT":
      return "text-green-700";
    case "FAILED":
    case "DEAD":
      return "text-red-700";
    default:
      return "text-amber-700";
  }
}

export default function MetaConversionLogsPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<MetaEventQueueStatus | "ALL">("ALL");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["meta-event-logs", page, status],
    queryFn: () =>
      hospitalAdminApi.listMetaConversionLogs({
        page,
        limit: 25,
        ...(status !== "ALL" ? { status } : {}),
      }),
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => hospitalAdminApi.retryMetaConversionEvent(id),
    onSuccess: () => {
      toast.success("Retry queued");
      qc.invalidateQueries({ queryKey: ["meta-event-logs"] });
    },
    onError: (e: Error) => toast.error(e.message || "Retry failed"),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 25));

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold">Meta Event Logs</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Audit trail for outbound Conversions API events (request/response per attempt).
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>Events</CardTitle>
          <Select value={status} onValueChange={(v) => { setStatus(v as typeof status); setPage(1); }}>
            <SelectTrigger className="w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All statuses</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="SENT">Sent</SelectItem>
              <SelectItem value="FAILED">Failed</SelectItem>
              <SelectItem value="DEAD">Dead</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No events yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-3">Time</th>
                    <th className="py-2 pr-3">Contact</th>
                    <th className="py-2 pr-3">THMS action</th>
                    <th className="py-2 pr-3">Meta event</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Revenue</th>
                    <th className="py-2 pr-3">Attempts</th>
                    <th className="py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row: MetaEventLogRow) => (
                    <Fragment key={row.id}>
                      <tr key={row.id} className="border-b align-top">
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {new Date(row.createdAt).toLocaleString()}
                        </td>
                        <td className="py-2 pr-3">
                          {row.lead ? (
                            <>
                              <div className="font-medium">{row.lead.patientName}</div>
                              <div className="text-xs text-muted-foreground">{row.lead.phone}</div>
                            </>
                          ) : row.patient ? (
                            <>
                              <div className="font-medium">{row.patient.name}</div>
                              <div className="text-xs text-muted-foreground">{row.patient.phone}</div>
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="py-2 pr-3">{ACTION_LABELS[row.thmsAction]}</td>
                        <td className="py-2 pr-3">{row.metaEventName}</td>
                        <td className={`py-2 pr-3 font-medium ${statusClass(row.status)}`}>
                          {row.status}
                          {row.lastErrorMessage ? (
                            <div className="text-xs font-normal text-muted-foreground max-w-xs truncate" title={row.lastErrorMessage}>
                              {row.lastErrorMessage}
                            </div>
                          ) : null}
                        </td>
                        <td className="py-2 pr-3">
                          {row.revenueAmount != null ? `₹${row.revenueAmount.toLocaleString()}` : "—"}
                        </td>
                        <td className="py-2 pr-3">{row.attemptCount}</td>
                        <td className="py-2 space-x-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                          >
                            {expandedId === row.id ? "Hide" : "Audit"}
                          </Button>
                          {(row.status === "FAILED" || row.status === "DEAD") && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={retryMutation.isPending}
                              onClick={() => retryMutation.mutate(row.id)}
                            >
                              Retry
                            </Button>
                          )}
                        </td>
                      </tr>
                      {expandedId === row.id && row.latestLog ? (
                        <tr key={`${row.id}-audit`} className="border-b bg-muted/30">
                          <td colSpan={8} className="py-3 px-2">
                            <div className="grid gap-3 md:grid-cols-2 text-xs">
                              <div>
                                <div className="font-medium mb-1">Request payload</div>
                                <pre className="overflow-auto max-h-48 rounded bg-background p-2 border">
                                  {JSON.stringify(row.latestLog.requestPayload, null, 2)}
                                </pre>
                              </div>
                              <div>
                                <div className="font-medium mb-1">Response payload</div>
                                <pre className="overflow-auto max-h-48 rounded bg-background p-2 border">
                                  {JSON.stringify(row.latestLog.responsePayload, null, 2)}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">{total} total</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <span className="text-sm self-center">Page {page} / {totalPages}</span>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
